import i18next from 'i18next';
import type { CurrencyCode } from '@/mocks/types';

/**
 * Central formatter helper — the NFR-024 contract.
 *
 * Every component that displays currency, dates, numbers, percentages, or
 * durations MUST route through here. The helper is locale-aware via the
 * active `i18next.language` and leverages the platform `Intl` APIs so we
 * never hand-roll locale rules.
 *
 * Consumer contract (design-system pillar):
 *   format.currency(amount, currencyCode?, opts?)
 *   format.date(date, preset)
 *   format.number(n, opts?)
 *   format.percent(n, fractionDigits?)
 *   format.duration(...)
 *
 * Design-system principle:
 *  - Components pass raw values and a `currencyCode` / `preset`.
 *  - Per-component locale/formatting calls are FORBIDDEN. Fail the review
 *    if any screen calls `toLocaleString` directly.
 *
 * AR note:
 *  - When `i18n.language === 'ar'`, the AR-SA locale is used. Digits default
 *    to Western Arabic (0-9) for numeric clarity; if the client later
 *    requests Eastern Arabic digits (٠-٩) flip `numberingSystem: 'arab'`
 *    here in ONE place.
 *  - Hijri calendar: `format.date(..., 'shortHijri')` uses `ar-SA-u-ca-islamic`.
 *    AR date pickers should show both Gregorian + Hijri side by side — the
 *    dayjs / AntD DatePicker handles this via the `ar-sa` locale.
 */

export type DatePreset = 'short' | 'long' | 'full' | 'monthYear' | 'shortHijri';

function currentLocale(): string {
  const lng = i18next.language ?? 'ar';
  // Normalize to a proper BCP-47 tag the Intl APIs understand.
  if (lng.startsWith('ar')) return 'ar-SA';
  return 'en-US';
}

interface CurrencyOptions {
  /** Min + max fraction digits. Default 2 for non-JPY, 0 for JPY-like codes (not in our list). */
  fractionDigits?: number;
  /** Render the code explicitly (e.g. "1,234.56 SAR") instead of the symbol. */
  codeDisplay?: 'symbol' | 'code' | 'name';
}

function formatCurrency(
  amount: number,
  currencyCode: CurrencyCode = 'SAR',
  opts: CurrencyOptions = {},
): string {
  const { fractionDigits = 2, codeDisplay = 'code' } = opts;
  return new Intl.NumberFormat(currentLocale(), {
    style: 'currency',
    currency: currencyCode,
    currencyDisplay: codeDisplay,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(amount);
}

interface NumberOptions {
  fractionDigits?: number;
  compact?: boolean;
}

function formatNumber(n: number, opts: NumberOptions = {}): string {
  const { fractionDigits, compact } = opts;
  return new Intl.NumberFormat(currentLocale(), {
    ...(fractionDigits !== undefined && {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }),
    ...(compact && { notation: 'compact', compactDisplay: 'short' }),
  }).format(n);
}

function formatPercent(n: number, fractionDigits = 1): string {
  return new Intl.NumberFormat(currentLocale(), {
    style: 'percent',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(n);
}

function formatDate(input: string | number | Date, preset: DatePreset = 'short'): string {
  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) return '—';

  const locale = currentLocale();

  switch (preset) {
    case 'long':
      return new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }).format(date);
    case 'full':
      return new Intl.DateTimeFormat(locale, {
        dateStyle: 'full',
        timeStyle: 'short',
      }).format(date);
    case 'monthYear':
      return new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: 'short',
      }).format(date);
    case 'shortHijri':
      return new Intl.DateTimeFormat('ar-SA-u-ca-islamic', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }).format(date);
    case 'short':
    default:
      return new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }).format(date);
  }
}

/**
 * Duration formatting. Accepts a number of minutes (most common in payroll /
 * timesheet / attendance) OR a custom `{ hours, minutes }` object. Extend as
 * new use-cases appear; keep it narrow for now.
 */
function formatDuration(input: number | { hours?: number; minutes?: number }): string {
  const hours = typeof input === 'number' ? Math.floor(input / 60) : input.hours ?? 0;
  const minutes = typeof input === 'number' ? input % 60 : input.minutes ?? 0;
  const locale = currentLocale();
  const hLabel = locale.startsWith('ar') ? 'س' : 'h';
  const mLabel = locale.startsWith('ar') ? 'د' : 'm';
  if (hours === 0) return `${minutes}${mLabel}`;
  if (minutes === 0) return `${hours}${hLabel}`;
  return `${hours}${hLabel} ${minutes}${mLabel}`;
}

export const format = {
  currency: formatCurrency,
  number: formatNumber,
  percent: formatPercent,
  date: formatDate,
  duration: formatDuration,
} as const;

export type Formatter = typeof format;
